import { observer } from 'mobx-react-lite';
import { FC } from 'react';

import { CallConnectionQuality } from '@gem/call/CallProviders/callProviders.constants';
import { Call } from '@gem/call/CallStore/Call';
import { useTranslation } from '@gem/modules/i18n';
import { DotLoader } from '@gem/ui-kit';

import { CallVideoResolution } from '../../../Model/callModel.types';
import styles from './CallState.styles.m.css';
import { CallTime } from './CallTime/CallTime';
import { ConnectionQuality } from './ConnectionQuality/ConnectionQuality';

interface ICallStateProps {
  call: Call | null;
  className?: string;
}

export const CallState: FC<ICallStateProps> = observer(({ call, className }) => {
  const { t } = useTranslation();

  if (!call) {
    return null;
  }

  if (call.isBusy) {
    return t('calls_busy');
  }

  if (call.isNoAnswer) {
    return t('calls_no_answer');
  }

  if (call.isCalling) {
    return (
      <div className={styles.isCalling}>
        {t('calls_calling_status')}
        <DotLoader className={styles.loader} />
      </div>
    );
  }

  if (
    call.isJoining ||
    call.connectionQuality === CallConnectionQuality.Lost ||
    call.connectionQuality === CallConnectionQuality.Unknown
  ) {
    return (
      <div className={styles.isCalling}>
        {t('calls_connecting_status')}
        <DotLoader className={styles.loader} />
      </div>
    );
  }

  if (call.isAnswered) {
    return (
      <div className={styles.activeCall}>
        <ConnectionQuality call={call} />
        <CallTime
          className={className}
          call={call}
        />
        {call.callLimits.videoQualitySettings &&
          call.callLimits.videoQualitySettings.high.resolution === CallVideoResolution.P1080 && (
            <span className={styles.fullHd}>{t('premium_feature_fullhd_icon')}</span>
          )}
      </div>
    );
  }

  return null;
});
